package com.tovtalk.ui.platform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.rememberUpdatedState
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * Web back handling.
 *
 * On Android, [PlatformBackHandler] intercepts the hardware/gesture back so an in-app
 * affordance (e.g. closing a full-screen viewer) runs instead of leaving the screen.
 * On web the equivalent is the browser Back button, which fires a `popstate` event.
 *
 * Strategy:
 * - When [enabled], we push a sentinel history entry (tagged in `history.state`) so the *next*
 *   browser-back lands "off" it and fires `popstate`. We catch that, invoke [onBack], and re-push
 *   the sentinel so repeated back presses keep being intercepted while [enabled] stays true.
 * - When disabled / disposed we pop our sentinel (history.back) if it's still current, so the user
 *   isn't left needing an extra back press to escape.
 *
 * Limitation: [WebUrlSync] also listens to `popstate` to mirror the URL into the NavController.
 * Because our sentinel re-uses the same URL (`location.href`), the popstate that pops it does not
 * change the path, so WebUrlSync's handler resolves to the same route and is effectively a no-op
 * for that event — the two coexist. We intentionally do NOT call `stopPropagation`, keeping
 * WebUrlSync's normal URL-driven navigation intact for all other (non-sentinel) popstate events.
 */
@Composable
actual fun PlatformBackHandler(enabled: Boolean, onBack: () -> Unit) {
    val currentOnBack = rememberUpdatedState(onBack)

    DisposableEffect(enabled) {
        if (!enabled) {
            return@DisposableEffect onDispose { }
        }

        // Push a sentinel entry so the next browser-back targets it and we can intercept.
        window.history.pushState(backHandlerSentinelState(), "", window.location.href)

        val listener: (Event) -> Unit = { _: Event ->
            // The browser popped our sentinel (or an entry below it). Run the in-app back and
            // re-arm by pushing a fresh sentinel so the next back press is also captured.
            currentOnBack.value()
            window.history.pushState(backHandlerSentinelState(), "", window.location.href)
        }
        window.addEventListener("popstate", listener)

        onDispose {
            window.removeEventListener("popstate", listener)
            // Remove our sentinel entry if it's still the current one.
            if (isBackHandlerSentinel(window.history.asDynamic().state)) {
                window.history.back()
            }
        }
    }
}

// JS interop helpers — kept tiny and pure so the `js(...)` strings reference no Kotlin locals.

/** Creates `{ __ttBackHandler: true }` to tag a history entry as ours. */
private fun backHandlerSentinelState(): Any =
    js("({ __ttBackHandler: true })")

/** True if [state] is a sentinel history.state we pushed. */
private fun isBackHandlerSentinel(state: Any?): Boolean {
    val s = state.asDynamic()
    return s != null && s.__ttBackHandler == true
}
